import React from 'react';

import Collapse from '~/components/ui/atoms/Collapse';
import cn from '~/Utils/cn';

interface OrderListProps {
  keys: string[];
  keyLabels: Record<string, string>;
  expanded?: boolean;
  /** The order these keys came from. When given, an item whose index changed is marked with its old → new
   * position; an item at the same index renders plain. */
  before?: string[];
  /** The keys the promotion actually names. Anything listed here that is absent from it survives by staying
   * put rather than by being promoted — the difference between "this stays" and "this was left out". */
  promotedKeys?: string[];
}

/** A collection's order as a numbered list of human labels (falling back to the raw key). */
const OrderList: React.FC<OrderListProps> = ({ keys, keyLabels, expanded = true, before, promotedKeys }) => (
  <Collapse in={expanded}>
    <ol className="my-0 list-decimal pl-16">
      {keys.map((key, idx) => {
        const fromIndex = before?.indexOf(key) ?? -1;
        const moved = fromIndex !== -1 && fromIndex !== idx;
        const keptInPlace = !moved && promotedKeys !== undefined && !promotedKeys.includes(key);
        return (
          <li key={key} className={cn({ 'font-semibold text-teal-800': moved })}>
            {keyLabels[key] ?? key}
            {moved && (
              <span className="ml-8 whitespace-nowrap text-xs font-normal text-teal-800">
                {`#${fromIndex + 1} → #${idx + 1}`}
              </span>
            )}
            {keptInPlace && <span className="ml-8 whitespace-nowrap text-xs italic text-slate-600">kept in place</span>}
          </li>
        );
      })}
    </ol>
  </Collapse>
);

export default OrderList;
